import { RouterStateSerializer } from '@ngrx/router-store';
import { RouterStateSnapshot, Params } from '@angular/router';
import { Pipe, PipeTransform, Injectable } from '@angular/core';

/**
 * The RouterStateSerializer takes the current RouterStateSnapshot
 * and returns any pertinent information needed. The snapshot contains
 * all information about the state of the router at the given point in time.
 * The entire snapshot is complex and not always needed. In this case, you only
 * need the URL and query parameters from the snapshot in the store. Other items could be
 * returned such as route parameters and static route data.
 */

export interface RouterStateUrl {
	url: string;
	params: Params;
	queryParams: Params;
}

@Injectable()
export class CustomRouterStateSerializer implements RouterStateSerializer<RouterStateUrl> {
	serialize(routerState: RouterStateSnapshot): RouterStateUrl {
		let route = routerState.root;

		while (route.firstChild) {
			route = route.firstChild;
		}

		const {
			url,
			root: { queryParams },
		} = routerState;
		const { params } = route;

		// Only return an object including the URL, params and query params
		// instead of the entire snapshot
		return { url, params, queryParams };
	}
}

export function orderByProperty(arr: any[], prop: string, ascending?: boolean):any[] {
	return arr
		? arr.sort(function (a, b) {
				if (a[prop] === null) {
					if (!ascending) {
						return 1;
					} else {
						return -1;
					}
				} else if (b[prop] === null) {
					if (!ascending) {
						return -1;
					} else {
						return 1;
					}
				}

				let aProp: any, bProp: any;
				if (typeof a[prop] === 'string') {
					aProp = a[prop].toLowerCase();
					bProp = b[prop].toLowerCase();
				} else {
					aProp = a[prop];
					bProp = b[prop];
				}

				if (!ascending) {
					if (aProp < bProp) {
						return -1;
					}
					if (aProp > bProp) {
						return 1;
					}
				} else {
					if (aProp > bProp) {
						return -1;
					}
					if (aProp < bProp) {
						return 1;
					}
				}
				return 0; // default return value (no sorting)
		  })
		: null;
}

export function orderReverseByProperty(arr: any[], prop: string) {
	return arr.sort(function (a, b) {
		const aProp = a[prop].toLowerCase(),
			bProp = b[prop].toLowerCase();
		if (aProp > bProp) {
			return -1;
		}
		if (aProp < bProp) {
			return 1;
		}
		return 0; // default return value (no sorting)
	});
}

export function capitalizeString(text: string) {
	return (text = text
		.split(' ')
		.map(s => s.charAt(0).toUpperCase() + s.substring(1))
		.join(' '));
}

export function merge(obj, src) {
	obj = {...obj} 
	
	Object.keys(src).forEach(function (key) {
		obj[key] = src[key];
	});
	
	return obj;
}

/**
  * @name: clearClassFromElements
  * @description: given an array of elements and a specific css class name, this function will remove the
  given class from each element.
  * @param {array} elements
  * @param {string} className
  * @returns: elements
  */
export function clearClassFromElements(elements, className) {
	for (let i = 0, len = elements.length; i < len; i++) {
		elements[i].classList.remove(className);
	}
	return elements;
}

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text, font) {
	// re-use canvas object for better performance
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	context.font = font;
	const metrics = context.measureText(text);
	return metrics.width;
}

@Pipe({
	name: 'filterUsers',
})
export class UserFilterPipe implements PipeTransform {
	transform(list: any, searchValue: string): any[] {
		if (!list) {
			return [];
		}
		if (!searchValue) {
			return list;
		}
		searchValue = searchValue.toLowerCase();

		return list.filter((item: any) => {
			console.log(item['firstName']);
			return (
				item['firstName'].toLowerCase().includes(searchValue) ||
				item['lastName'].toLowerCase().includes(searchValue) ||
				item['email'].toLowerCase().includes(searchValue)
			);
		});
	}
}

@Pipe({
	name: 'filterGroupsAndCategories',
	pure: false,
})
export class GroupCategoryPipe implements PipeTransform {
	transform(items: any, searchValue: string): any[] {
		const list = items ? [...items] : null;
		if (!list) {
			return [];
		}
		if (!searchValue) {
			return list;
		}

		const filteredOpen = filterTree(list, searchValue);
		return filteredOpen.map(obj => {
			obj.collapsed = false;
			return obj;
		});
	}
}

@Pipe({
	name: 'filterRoles',
})
export class RoleFilterPipe implements PipeTransform {
	transform(list: any, searchValue: string): any[] {
		if (!list) {
			return [];
		}
		if (!searchValue) {
			return list;
		}
		searchValue = searchValue.toLowerCase();

		return list.filter((it: any) => {
			let contains = false;
			for (const prop in it) {
				if (it.hasOwnProperty(prop)) {
					if (contains) {
						return contains;
					}
					if (prop === 'name') {
						contains = it[prop].toLowerCase().includes(searchValue);
					}
				}
			}
			return contains;
		});
	}
}

@Pipe({
	name: 'filterCapabilities',
})
export class CapabilityFilterPipe implements PipeTransform {
	transform(capabilityGroups: any, searchValue: string): any[] {
		if (!capabilityGroups) {
			return [];
		}
		if (!searchValue) {
			return capabilityGroups;
		}
		searchValue = searchValue.toLowerCase();

		return capabilityGroups
			.map(capabilityGroup => {
				return capabilityGroup.filter((capability: any) => {
					let contains = false;
					for (const prop in capability) {
						if (capability.hasOwnProperty(prop)) {
							if (contains) {
								return contains;
							}
							if (prop === 'name') {
								contains = capability[prop].toLowerCase().includes(searchValue);
							}
						}
					}
					return contains;
				});
			})
			.filter(capabilityGroup => capabilityGroup.length > 0);
	}
}

function filterTree(array, text) {
	return JSON.parse(JSON.stringify(array)).filter(function iter(o) {
		let temp;
		if (o.name.toLowerCase().includes(text.toLowerCase())) {
			return true;
		}
		if (!Array.isArray(o.categories)) {
			return false;
		}
		temp = o.categories.filter(iter);
		if (temp.length) {
			o.categories = temp;
			return true;
		}
	});
}

export function copy(item) {
	let result = null;
	if (!item) {
		return result;
	}
	if (Array.isArray(item)) {
		result = [];
		item.forEach(element => {
			result.push(copy(element));
		});
	} else if (item instanceof Object && !(item instanceof Function)) {
		result = {};
		for (const key in item) {
			if (key) {
				result[key] = copy(item[key]);
			}
		}
	}
	return result || item;
}

/**
 * Returns true is the current browser is IE or Edge, else false
 */
export function browserIsIE() {
	const browserUserAgent = navigator.userAgent;
	return browserUserAgent.indexOf('Trident') > -1 || browserUserAgent.indexOf('Edge') > -1;
}

/**
 * Returns true is the current browser is IE11, else false
 */
export function browserIsIE11() {
	const browserUserAgent = navigator.userAgent;
	return browserUserAgent.indexOf('Trident') > -1;
}

export function fileToFormData(file: File, compressionResult): FormData {
	const fd = new FormData();
	// File() constructor not available in IE11, so this mocks the required properties by extending the Blob type
	const imageFile: any = new Blob([compressionResult], { type: file.type });
	imageFile.lastModifiedDate = file.lastModified;
	fd.append('file', imageFile, file.name);
	return fd;
}

export function fileToFormDataWithoutCompression(file: File): FormData {
	const fd = new FormData();
	// File() constructor not available in IE11, so this mocks the required properties by extending the Blob type
	const imageFile: any = new Blob([file], { type: file.type });
	imageFile.lastModifiedDate = file.lastModified;
	fd.append('file', imageFile, file.name);
	return fd;
}

export function convertToFile(file: File, result: any): File {
	const imageFile: any = new Blob([result], { type: file.type });
	imageFile.lastModifiedDate = file.lastModified;
	imageFile.name = file.name;
	return imageFile;
}

export function getBoundaryColor(currentBoundaryColorIndex: number): string {
	let color = '';
	switch (currentBoundaryColorIndex) {
		case 0:
			color = BoundaryColors.Blue;
			break;
		case 1:
			color = BoundaryColors.Green;
			break;
		case 2:
			color = BoundaryColors.Yellow;
			break;
		case 3:
			color = BoundaryColors.Orange;
			break;
		case 4:
			color = BoundaryColors.Pink;
			break;
		case 5:
			color = BoundaryColors.Purple;
			break;
		default:
			color = BoundaryColors.White;
			break;
	}

	return color;
}

export enum BoundaryColors {
	Blue = '#5389CE',
	Green = '#47CDAB',
	Yellow = '#FFBF58',
	Orange = '#FF9658',
	Pink = '#E6509A',
	Purple = '#8755D1',
	White = '#ffffff',
}

export function addItemToTree(list: any, item: any) {
	let items = list.map(listItem => {
		if (listItem.id === item.parentId) {
			listItem.children.push(item);
		} else if (listItem.children && listItem.children.length > 0) {
			listItem.children = addItemToTree(listItem.children, item);
		}
		return listItem;
	});
	return items;
}

export function addItemToParentChildTree(parent: any, item: any) {
	if (parent.id === item.parentId) {
		parent.child = item;
	} else if (parent.child) {
		parent.child = addItemToParentChildTree(parent.child, item);
	}
	return parent;
}
